"use client";
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "./assets/DreamFrame Studio Logo.png";
import {
  Sparkles,
  PlayCircle,
  ShieldCheck,
  Handshake,
  Rocket,
  Users,
  Wand2,
  Megaphone,
  ArrowRight,
  ChevronDown,
  Star,
  X,
} from "lucide-react";
/* Try to load logo; fall back gracefully if file is absent */
let logoSrc;
try {
  // If present, keep exact name or adjust the path accordingly.
  // eslint-disable-next-line global-require
  logoSrc = require("./assets/DreamFrame Studio Logo.png");
} catch {
  logoSrc = null;
}

/**
 * Dreamframe Studio — Landing Page
 * Tailwind CSS + Framer Motion + Lucide icons
 * (Look & feel preserved, CTA wired to Supabase with toast banners)
 */

// ---------- Helpers ----------
const section = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";
const gradientText =
  "bg-gradient-to-r from-indigo-300 via-fuchsia-300 to-pink-300 bg-clip-text text-transparent";

// Soft shimmering divider
const Divider = () => (
  <div className="relative mx-auto my-24 h-px w-full max-w-6xl overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
  </div>
);

// Subtle glowing background FX
const BackgroundFX = () => (
  <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
    {/* Gradient mesh */}
    <div className="absolute -top-32 right-[-10%] h-[38rem] w-[38rem] rounded-full bg-[radial-gradient(circle_at_center,rgba(90,75,219,0.35),rgba(255,255,255,0)_60%)] blur-3xl" />
    <div className="absolute -bottom-28 left-[-10%] h-[40rem] w-[40rem] rounded-full bg-[radial-gradient(circle_at_center,rgba(236,72,153,0.25),rgba(255,255,255,0)_60%)] blur-3xl" />
    {/* Mesh ribbons */}
    <motion.div
      initial={{ opacity: 0.15, y: 20 }}
      animate={{ opacity: 0.25, y: 0 }}
      transition={{ duration: 1.5, ease: "easeOut" }}
      className="absolute left-1/2 top-1/2 h-[120vmax] w-[120vmax] -translate-x-1/2 -translate-y-1/2"
      style={{
        background:
          "conic-gradient(from 180deg at 50% 50%, rgba(99,102,241,0.08), rgba(236,72,153,0.08), rgba(14,165,233,0.08), rgba(99,102,241,0.08))",
        maskImage:
          "radial-gradient(circle at center, black 40%, transparent 60%, transparent)",
        WebkitMaskImage:
          "radial-gradient(circle at center, black 40%, transparent 60%, transparent)",
      }}
    />
    {/* Dot grid overlay */}
    <div
      className="absolute inset-0 opacity-[.07]"
      style={{
        backgroundImage:
          "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    />
  </div>
);

/* ---------- Toast banner (replaces alerts) ---------- */
function Toast({ kind = "success", message, onClose }) {
  const base =
    "pointer-events-auto mx-auto mb-3 w-full max-w-xl rounded-xl border p-3 text-sm backdrop-blur";
  const styles =
    kind === "success"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
      : "border-rose-500/30 bg-rose-500/10 text-rose-200";
  return (
    <motion.div
      initial={{ y: -10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -10, opacity: 0 }}
      className={`${base} ${styles} flex items-start gap-2`}
      role="status"
      aria-live="polite"
    >
      <div className="mt-0.5">{kind === "success" ? "✅" : "⚠️"}</div>
      <div className="flex-1">{message}</div>
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg border border-white/10 px-2 py-1 text-xs text-white/70 hover:bg-white/10"
      >
        <X className="inline h-3.5 w-3.5 -mt-0.5" /> Close
      </button>
    </motion.div>
  );
}

// ---------- Navbar (mobile-safe, desktop unchanged) ----------
const Navbar = () => (
  <header className="sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-gray-950/40">
    <nav className={`${section} flex h-16 items-center justify-between`}>
      <div className="flex min-w-0 items-center gap-3">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt="Dreamframe Studio Logo"
            className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl object-contain shadow-lg shadow-fuchsia-500/30"
          />
        ) : (
          <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 shadow-lg shadow-fuchsia-500/30" />
        )}
        <span className="text-base sm:text-lg font-semibold tracking-tight text-white">
          Dreamframe
        </span>
        <span className="ml-1.5 sm:ml-2 flex-shrink-0 rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 px-2 py-0.5 sm:px-2.5 text-xs sm:text-sm font-semibold text-fuchsia-200">
          Studio
        </span>
      </div>

      <div className="hidden items-center gap-8 text-sm text-white/70 md:flex">
        <a className="hover:text-white" href="#features">Features</a>
        <a className="hover:text-white" href="#how-it-works">How it works</a>
        <a className="hover:text-white" href="#why-join">Why Join</a>
        <a className="hover:text-white" href="#trust">Trust</a>
        <a className="hover:text-white" href="#about">About</a>
        <a className="hover:text-white" href="#testimonials">Testimonials</a>
        {/*<a className="hover:text-white" href="#pricing">Pricing</a>*/}
        <a className="hover:text-white" href="#faq">FAQ</a>
      </div>

      <div className="flex items-center gap-3">
        {/* Mobile: compact button so it doesn’t overlap the Studio pill */}
        <a
          href="/login"
          className="md:hidden inline-flex items-center gap-1 rounded-2xl bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-fuchsia-500/30 transition hover:brightness-110"
        >
          Login
        </a>

        {/* Desktop / tablet: keep existing CTA unchanged */}
        <a
          href="/login"
          className="hidden md:inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-fuchsia-500/30 transition hover:brightness-110"
        >
          Login / Get Started
        </a>
      </div>
    </nav>
  </header>
);



// ---------- Hero ----------
const Hero = () => (
  <section className={`${section} relative pt-20 sm:pt-28`}>
    <div className="mx-auto max-w-4xl text-center">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-balance text-4xl font-bold leading-tight text-white sm:text-6xl"
      >
        Collaborate. <span className={gradientText}>Animate.</span> Distribute.
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.6 }}
        className="mx-auto mt-5 max-w-2xl text-pretty text-lg text-white/70"
      >
        A Global Stage for Indie Creators
        — Powered by Human‑Written Scripts, Smart Collaboration, Fair Pay, OTT-Ready Exports and AI That Speeds Production.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row"
      >
       {/* <a
          href="#cta"
          className="group inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-fuchsia-500/30 transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
        >
          Join the Beta
          <Sparkles className="h-5 w-5 transition group-hover:rotate-12" />
        </a>
        
        🏆 NEW CONTEST BUTTON WITH ANIMATED SHIMMER 
          <motion.a
            href="/contest"
            initial={{ scale: 1, opacity: 0.9 }}
            animate={{
              scale: [1, 1.05, 1],
              opacity: [0.9, 1, 0.9],
            }}
            transition={{
              repeat: Infinity,
              duration: 3,
              ease: "easeInOut",
            }}
            className="group inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-amber-500/30 transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            🏆 Win $1000
            <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
          </motion.a> */}
        <a
          href="#creators"
          className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-6 py-3 text-base font-semibold text-white backdrop-blur transition hover:bg-white/10"
        >
          <PlayCircle className="h-5 w-5" /> See the Flow
        </a>
      </motion.div>
    </div>

    {/* Hero visual (unchanged) */}
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.25, duration: 0.7, ease: "easeOut" }}
      className="relative mx-auto mt-14 max-w-6xl"
    >
      <div className="relative rounded-3xl border border-white/10 bg-white/5 p-2 shadow-2xl backdrop-blur">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900/60 to-slate-800/40">
          {/* Mock app frame */}
          <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2 text-xs text-white/60">
            <div className="flex gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-rose-400/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-300/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-300/70" />
            </div>
            <span>dreamframe.studio/collab</span>
          </div>

          {/* Parallax content rows */}
          <div className="grid gap-6 p-6 sm:grid-cols-2">
            {/* Left: Matching */}
            <motion.div whileHover={{ y: -4 }} className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="mb-3 flex items-center gap-2 text-white/80">
                <Users className="h-5 w-5" />
                <span className="text-sm">CCHI™ Smart Matching</span>
              </div>
              <div className="h-40 rounded-xl bg-gradient-to-br from-indigo-500/20 via-fuchsia-500/20 to-cyan-400/20 p-4">
                <p className="text-sm text-white/70">
                  Auto-match scriptwriters with AI animators & voice artists based on
                  genre, style, and delivery speed.
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-white/70">
                  {["Horror", "Sci-Fi", "Kids & Family"].map((g) => (
                    <div key={g} className="rounded-lg bg-black/30 p-2">{g}</div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right: Escrow */}
            <motion.div whileHover={{ y: -4 }} className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="mb-3 flex items-center gap-2 text-white/80">
                <ShieldCheck className="h-5 w-5" />
                <span className="text-sm">Escrow-Secured Milestones</span>
              </div>
              <div className="h-40 rounded-xl bg-gradient-to-br from-emerald-500/20 via-sky-500/20 to-indigo-500/20 p-4">
                <p className="text-sm text-white/70">
                  Fund, deliver, and release with transparent contracts and automatic
                  payouts.
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-white/80">
                  {[
                    { k: "Funded", v: "$5,000" },
                    { k: "Released", v: "$3,250" },
                    { k: "Balance", v: "$1,750" },
                  ].map((i) => (
                    <div key={i.k} className="rounded-lg bg-black/30 p-2">
                      <div className="text-white/50">{i.k}</div>
                      <div className="font-semibold">{i.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Left: AI tools */}
            <motion.div whileHover={{ y: -4 }} className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="mb-3 flex items-center gap-2 text-white/80">
                <Wand2 className="h-5 w-5" />
                <span className="text-sm">AI Pre-Production</span>
              </div>
              <div className="h-40 rounded-xl bg-gradient-to-br from-fuchsia-500/20 via-pink-500/20 to-rose-500/20 p-4">
                <p className="text-sm text-white/70">
                  Storyboards, voice previews, and shot-lists generated from your
                  script.
                </p>
                <div className="mt-3 flex gap-2 text-xs text-white/80">
                  {["Storyboard", "Voice", "Shot-list"].map((t) => (
                    <span key={t} className="rounded-lg bg-black/30 px-2 py-1">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right: Distribution */}
            <motion.div whileHover={{ y: -4 }} className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="mb-3 flex items-center gap-2 text-white/80">
                <Megaphone className="h-5 w-5" />
                <span className="text-sm">OTT-Ready Exports</span>
              </div>
              <div className="h-40 rounded-xl bg-gradient-to-br from-cyan-400/20 via-sky-400/20 to-indigo-500/20 p-4">
                <p className="text-sm text-white/70">
                  Package deliverables for aggregators & buyers like ShortsTV and
                  Filmhub.
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-white/80">
                  {["4K", "ProRes", "Captions"].map((t) => (
                    <div key={t} className="rounded-lg bg-black/30 p-2 text-center">
                      {t}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="pointer-events-none absolute -bottom-10 left-1/2 h-40 w-[70%] -translate-x-1/2 rounded-full bg-fuchsia-500/30 blur-3xl"
      />
    </motion.div>

    <div className="mt-10 flex items-center justify-center text-white/60">
      <ChevronDown className="h-6 w-6 animate-bounce" />
    </div>
  </section>
);

// ---------- Logo Strip ----------
const Logos = () => (
  <section className={`${section} mt-20`}>
    <p className="mb-6 text-center text-sm uppercase tracking-wider text-white/40">
      Works with your favorite AI tools & distributors
    </p>
    <div className="grid grid-cols-2 items-center gap-6 opacity-80 sm:grid-cols-3 md:grid-cols-6">
      {["Runway", "Midjourney", "Veo 3", "ElevenLabs", "Kling", "ShortsTV"].map(
        (name) => (
          <div
            key={name}
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-sm text-white/70 backdrop-blur hover:bg-white/10"
          >
            {name}
          </div>
        )
      )}
    </div>

    {/* Subline below the logos */}
    <p className="mt-6 text-center text-xs text-white/50 max-w-2xl mx-auto">
    Dreamframe works with today’s leading AI tools — use your own accounts to
  create deliverables now, while we actively partner to bring these platforms
  directly into your workflow in the future.
    </p>
  </section>
);


// ---------- Stats / Problem ----------
const Stats = () => (
  <section className={`${section} mt-24`}>
    <div className="mx-auto max-w-4xl text-center">
      <h2 className="text-3xl font-semibold text-white sm:text-4xl">
        The problem indie creators face
      </h2>
      <p className="mx-auto mt-3 max-w-2xl text-white/70">
        Finding reliable collaborators, financing milestones, and packaging for OTT
        buyers shouldn’t be this hard.
      </p>
    </div>

    <div className="mt-10 grid gap-6 sm:grid-cols-3">
      {[
        {
          k: "97%",
          t: "struggle to find collaborators",
          d: "Stage32 Filmmaker Survey, 2023",
        },
        { k: "$3k–$50k", t: "per minute to animate", d: "US Pricing Benchmarks" },
        { k: "70%", t: "can’t monetize reliably", d: "Creator Economy Reports" },
      ].map((s) => (
        <motion.div
          key={s.k}
          whileHover={{ y: -4 }}
          className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur"
        >
          <div className="text-4xl font-bold text-white">{s.k}</div>
          <div className="mt-2 text-white/80">{s.t}</div>
          <div className="mt-1 text-xs text-white/50">{s.d}</div>
        </motion.div>
      ))}
    </div>
    {/* NEW: Cost Snapshot strip (non-breaking, sits below the grid) */}
    <div className="mx-auto mt-8 max-w-4xl">
      <div className="rounded-2xl border border-white/10 bg-gradient-to-r from-white/5 to-white/0 p-5 backdrop-blur">
        <div className="grid gap-4 sm:grid-cols-2">
          <motion.div
            whileHover={{ y: -2 }}
            className="rounded-xl border border-white/10 bg-white/5 p-4"
          >
            <div className="text-sm uppercase tracking-wide text-white/50">Total “DIY” Cost Without Dreamframe Studio</div>
            <div className="mt-1 text-2xl font-semibold text-white">$400–$1,000</div>
            <div className="mt-1 text-white/70 text-sm">Typical cost <span className="text-white/60">per project</span> just to find, manage, protect, and deliver.</div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="rounded-xl border border-white/10 bg-white/5 p-4"
          >
            <div className="text-sm uppercase tracking-wide text-white/50">Ongoing DIY Tools & Networking</div>
            <div className="mt-1 text-2xl font-semibold text-white">$50–$150</div>
            <div className="mt-1 text-white/70 text-sm">Even light users spend this <span className="text-white/60">per month</span> on memberships & management tools.</div>
          </motion.div>
        </div>

        {/* Optional value callout */}
        <p className="mt-4 text-center text-xs text-white/60">
          Dreamframe Pro consolidates discovery, escrow, and OTT-ready delivery — often replacing hundreds in DIY costs with a single plan.
        </p>
      </div>
    </div>
  </section>
);

// ---------- Features ----------
const features = [
  {
    icon: <Users className="h-5 w-5" />,
    title: "CCHI™ Smart Matching",
    desc: "Harmonize teams by genre, pace, and creative fit to cut onboarding time by 70%.",
  },
  {
    icon: <Wand2 className="h-5 w-5" />,
    title: "AI-Ready Workflow",
    desc: "Bring your own AI tools (Runway, Pika, ElevenLabs, etc.) to create storyboards, voice previews, and shot-lists today. Dreamframe is building direct integrations for a seamless future.",
  },
  {
    icon: <ShieldCheck className="h-5 w-5" />,
    title: "Escrow-Secured Milestones",
    desc: "Transparent funding, delivery sign-off, and instant payouts on acceptance.",
  },
  {
    icon: <Megaphone className="h-5 w-5" />,
    title: "OTT-Ready Exports",
    desc: "One-click packaging for aggregators and buyers. Captions, metadata, and 4K.",
  },
  {
    icon: <Handshake className="h-5 w-5" />,
    title: "Revenue Sharing",
    desc: "Crystal-clear splits and automated accounting with downloadable statements.",
  },
  {
    icon: <Rocket className="h-5 w-5" />,
    title: "Built for Creators",
    desc: "A modern, responsive platform designed to keep projects flowing smoothly from idea to delivery.",
  },
];

const Features = () => (
  <section id="features" className={`${section} mt-24 scroll-mt-24`}>
    <div className="mx-auto max-w-3xl text-center">
      <h2 className="text-3xl font-semibold text-white sm:text-4xl">Features</h2>
      <p className="mt-3 text-white/70">
        Everything you need to build a world-class indie pipeline — from first draft
        to distribution.
      </p>
    </div>

    <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {features.map((f) => (
        <motion.div
          key={f.title}
          whileHover={{ y: -4 }}
          className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur"
        >
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70">
            {f.icon}
            <span>Capability</span>
          </div>
          <h3 className="text-lg font-semibold text-white">{f.title}</h3>
          <p className="mt-2 text-sm text-white/70">{f.desc}</p>
          <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-gradient-to-br from-fuchsia-500/20 to-indigo-500/20 blur-2xl transition group-hover:scale-110" />
        </motion.div>
      ))}
    </div>
  </section>
);

// ---------- How it works ----------
const steps = [
  {
    title: "Post your project",
    desc: "Describe genre, tone, budget, and timeline. CCHI™ pre-filters candidates.",
    badge: "1",
  },
  {
    title: "Get matched fast",
    desc: "See top creators with style & speed scores. Chat and shortlist in minutes.",
    badge: "2",
  },
  {
    title: "Fund milestones",
    desc: "Lock scope, fund escrow, and track deliverables with crystal-clear status.",
    badge: "3",
  },
  {
    title: "Ship to OTT",
    desc: "Export 4K deliverables, captions, and metadata for buyers and aggregators.",
    badge: "4",
  },
];

const HowItWorks = () => (
  <section id="how-it-works" className={`${section} mt-24 scroll-mt-24`}>
    <div className="mx-auto max-w-3xl text-center">
      <h2 className="text-3xl font-semibold text-white sm:text-4xl">How it works</h2>
      <p className="mt-3 text-white/70">
        A straight path from idea to income — with fewer tabs, emails, and headaches.
      </p>
    </div>

    <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {steps.map((s) => (
        <motion.div
          key={s.title}
          whileHover={{ y: -4 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          className="relative rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur"
        >
          <div className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-tr from-indigo-500 to-fuchsia-500 text-sm font-semibold text-white">
            {s.badge}
          </div>
          <h3 className="text-lg font-semibold text-white">{s.title}</h3>
          <p className="mt-2 text-sm text-white/70">{s.desc}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

// ---------- Role-specific lite ----------
const RoleHowItWorksLite = () => (
  <section id="creators" className={`${section} mt-24 scroll-mt-24`}>
    <div className="mx-auto max-w-3xl text-center">
      <h2 className="text-3xl font-semibold text-white sm:text-4xl">
        Built for every creator
      </h2>
      <p className="mt-3 text-white/70">
        Writers, AI animators, voice artists, musicians, directors and producers — all in one protected pipeline.
      </p>
    </div>
    <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {[
        { t: "Scriptwriters", d: "Find animators & voice quickly with CCHI™ smart matching." },
        { t: "AI Animators", d: "Lock scope, milestones, and deliverables with escrow." },
        { t: "Voice Artists", d: "Share tone previews, nail direction early, reduce redo." },
        { t: "Directors / Producers", d: "Track status, budgets, and exports for OTT submission." },
      ].map((r) => (
        <div key={r.t} className="rounded-2xl border border-white/10 bg-white/5 p-5 text-white/80">
          <div className="text-white font-semibold">{r.t}</div>
          <p className="mt-2 text-sm text-white/70">{r.d}</p>
        </div>
      ))}
    </div>
  </section>
);

// ---------- Promo Video ----------
const PromoVideo = () => (
  <section className={`${section} mt-24`}>
    <div className="mx-auto max-w-4xl">
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-2 backdrop-blur">
        <div className="aspect-video w-full overflow-hidden rounded-xl">
          <iframe
            title="Dreamframe Studio Promo"
            className="h-full w-full"
            src="https://www.youtube.com/embed/ksTiIzYYF_M?autoplay=0&mute=1&rel=0&modestbranding=1&playsinline=1"
            allow="autoplay; encrypted-media"
            loading="lazy"
          />
        </div>
      </div>
      <p className="mt-3 text-center text-sm text-white/60">
        Unmute on YouTube for full audio experience 🔊
      </p>
    </div>
  </section>
);

// ---------- Why Join ----------
const WhyJoin = () => {
  const cards = [
    {
      icon: <Users className="h-5 w-5" />,
      title: "Find Your Team Instantly",
      desc: "Match with trusted scriptwriters, AI animators, and voice artists via CCHI™.",
    },
    {
      icon: <Wand2 className="h-5 w-5" />,
      title: "Accelerate Production",
      desc: "Storyboards, voice previews, and project setup in hours—not weeks.",
    },
    {
      icon: <ShieldCheck className="h-5 w-5" />,
      title: "Fair & Secure Payments",
      desc: "Escrow-protected milestones and clear revenue sharing.",
    },
    {
      icon: <Megaphone className="h-5 w-5" />,
      title: "OTT-Ready Distribution",
      desc: "Export packages ready for buyers like ShortsTV & Filmhub.",
    },
  ];

  return (
    <section id="why-join" className={`${section} mt-24 scroll-mt-24`}>
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-3xl font-semibold text-white sm:text-4xl">
          Why Join Dreamframe Studio?
        </h2>
        <p className="mt-3 text-white/70">
          Clean, modular benefits—just like your favorite modern SaaS.
        </p>
      </div>

      <div className="mt-10 grid gap-6 sm:grid-cols-2">
        {cards.map((c) => (
          <motion.div
            key={c.title}
            whileHover={{ y: -4 }}
            className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur"
          >
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70">
              {c.icon}
              <span>Benefit</span>
            </div>
            <h3 className="text-lg font-semibold text-white">{c.title}</h3>
            <p className="mt-2 text-sm text-white/70">{c.desc}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        {[
          "Early access spotlights",
          "Bring-your-own tools (Runway, Pika, ElevenLabs)",
          "Keep your IP — license on your terms",
        ].map((perk) => (
          <div
            key={perk}
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-sm text-white/70"
          >
            {perk}
          </div>
        ))}
      </div>
    </section>
  );
};

// ---------- Protected Collaboration / Creative Freedom ----------
const ProtectedCollab = () => (
  <section id="trust" className={`${section} mt-24 scroll-mt-24`}>
    <div className="mx-auto max-w-3xl text-center">
      <h2 className="text-3xl font-semibold text-white sm:text-4xl">
        Protected Collaboration. Creative Freedom.
      </h2>
      <p className="mt-3 text-white/70">
        Two principles. One workflow that keeps creators safe and fast.
      </p>
    </div>

    <div className="mt-10 grid gap-6 lg:grid-cols-2">
      {/* Left Card: Protected Collaboration */}
      <motion.div
        whileHover={{ y: -4 }}
        className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur"
      >
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70">
          <ShieldCheck className="h-4 w-4" />
          <span>Protected Collaboration</span>
        </div>
        <ul className="space-y-2 text-sm text-white/75">
          <li>Private, invite-only project spaces</li>
          <li>Mutual NDA terms applied automatically</li>
          <li>Built-in licensing templates (one-time + rev-share)</li>
          <li>Escrow-secured milestones and transparent payouts</li>
          <li>You own your work unless you license it</li>
        </ul>
      </motion.div>

      {/* Right Card: Creative Freedom */}
      <motion.div
        whileHover={{ y: -4 }}
        className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur"
      >
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70">
          <Sparkles className="h-4 w-4" />
          <span>Creative Freedom</span>
        </div>
        <ul className="space-y-2 text-sm text-white/75">
          <li>Focus on story—logistics handled by workflow automation</li>
          <li>AI pre-production to lock tone early and reduce redo</li>
          <li>Single dashboard for status, budgets, and deliverables</li>
          <li>OTT-ready exports (4K, captions, metadata) in one click</li>
          <li>Grow from short to series with clear, trackable progress</li>
        </ul>
      </motion.div>
    </div>
  </section>
);

// ---------- About ----------
const About = () => (
  <section id="about" className={`${section} mt-24 scroll-mt-24`}>
    <div className="mx-auto max-w-3xl text-center">
      <h2 className="text-3xl font-semibold text-white sm:text-4xl">
        About Dreamframe Studio
      </h2>
      <p className="mt-3 text-white/70">
        We remove gatekeepers so independent creators can ship beautiful work — fast and fairly.
      </p>
      <p className="mt-2 text-white/70">
        Story-first workflows, built-in protection, and distribution-ready outputs.
      </p>
      <p className="mt-2 text-white/70">
        Every creator on Dreamframe is vetted to ensure unmatched quality. By maintaining the highest standards, 
        we guarantee that deliverables meet professional benchmarks — empowering indie teams to compete globally.
      </p>
    </div>

    {/* Highlight card */}
    <div className="mx-auto mt-10 max-w-md">
      <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-6 backdrop-blur">
        <div className="flex items-center gap-2 text-emerald-300">
          <ShieldCheck className="h-5 w-5" />
          <span className="text-sm font-semibold uppercase tracking-wide">
            Vetted Creators
          </span>
        </div>
        <p className="mt-3 text-sm text-white/80">
          Only top-quality scriptwriters, animators, voice artists, and producers — 
          carefully vetted to maintain the highest standards of creativity and professionalism.
        </p>
      </div>
    </div>
  </section>
);


// ---------- Testimonials ----------
const Testimonials = () => (
  <section id="testimonials" className={`${section} mt-24 scroll-mt-24`}>
    <div className="mx-auto max-w-3xl text-center">
      <h2 className="text-3xl font-semibold text-white sm:text-4xl">Creators love it</h2>
      <p className="mt-3 text-white/70">
        Real teams shipping real stories — faster, safer, and with better outcomes.
      </p>
    </div>

    <div className="mt-10 grid gap-6 md:grid-cols-3">
      {[
        {
          name: "Ava J. — Scriptwriter",
          text: "Found my AI Animator in 48 hours. The CCHI\u2122 match was on point. He brought my vision to life exactly as I imagined.",
        },
        {
          name: "Rita S. — Director",
          text: "DreamFrame Studio combines thoughtful collaboration tools. From creator discovery to project creation, everything feels so streamlined.",
        },
        {
          name: "Liam C. — AI Animator",
          text: "The collaboration felt effortless — it’s like the platform already knew my style. I can easily see this becoming a go-to platform for creative teams looking to work smarter together.",
        },
      ].map((t) => (
        <motion.figure
          key={t.name}
          whileHover={{ y: -4 }}
          className="rounded-2xl border border-white/10 bg-white/5 p-6 text-white/80 backdrop-blur"
        >
          <div className="mb-3 flex items-center gap-1 text-amber-300">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-current" />
            ))}
          </div>
          <blockquote className="text-sm">“{t.text}”</blockquote>
          <figcaption className="mt-3 text-xs text-white/60">{t.name}</figcaption>
        </motion.figure>
      ))}
    </div>
  </section>
);


// ---------- REAL Supabase-backed CTA (Full name, Email, Primary role, Country + toast) ----------
const CTA = () => {
  const [toast, setToast] = useState(null); // { kind: 'success'|'error', message: string }
  return (
    <section id="cta" className={`${section} mt-24 scroll-mt-24`}>
      <AnimatePresence>
        {toast && (
          <Toast
            kind={toast.kind}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </AnimatePresence>
      {/*
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-indigo-600/30 via-fuchsia-600/20 to-cyan-500/20 p-8 shadow-2xl backdrop-blur">
        <div className="grid items-center gap-8 md:grid-cols-2">
          <div>
            <h3 className="text-2xl font-semibold text-white sm:text-3xl">
              Be an early creator. Own the edge.
            </h3>
            <p className="mt-2 text-white/70">
            Join the private beta. Lock in early perks and help shape the future of indie filmmaking powered by AI collaboration.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-white/80">
              <li className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" /> Escrow protection
              </li>
              <li className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" /> Transparent revenue splits
              </li>
              <li className="flex items-center gap-2">
                <Rocket className="h-4 w-4" /> OTT-ready packaging
              </li>
            </ul>
          </div>

          {/* Early Access Form 
          <form
            className="rounded-2xl border border-white/10 bg-white/10 p-4 sm:p-6"
            onSubmit={submitEarlyAccess}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs text-white/70">Full name</label>
                <input
                  required
                  type="text"
                  placeholder="Alex Rivera"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-white placeholder-white/40 outline-none focus:border-fuchsia-400/50"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs text-white/70">Email</label>
                <input
                  required
                  type="email"
                  placeholder="you@studio.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-white placeholder-white/40 outline-none focus:border-fuchsia-400/50"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-white/70">Primary role</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-fuchsia-400/50"
                >
                  <option>Scriptwriter</option>
                  <option>AI Animator</option>
                  <option>Voice Artist</option>
                  <option>Music Composer</option>
                  <option>Director</option>
                  <option>Producer</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-white/70">Country</label>
                <select
                  required
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-fuchsia-400/50"
                >
                  <option value="">Select your country</option>
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="India">India</option>
                  <option value="Australia">Australia</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="Brazil">Brazil</option>
                  <option value="Japan">Japan</option>
                  <option value="South Korea">South Korea</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500 px-5 py-3 font-semibold text-white shadow-lg shadow-fuchsia-500/30 transition hover:brightness-110 disabled:opacity-60"
              >
                {loading ? "Submitting..." : "Request Early Access"}{" "}
                <ArrowRight className="h-4 w-4" />
              </button>
              <p className="sm:col-span-2 text-center text-xs text-white/60">
                No spam. Beta invites go out weekly.
              </p>
            </div>
          </form>
        </div>
      </div> */}
      
    </section>
  );
};

// ---------- FAQ ----------
const faqs = [
  {
    q: "Is Dreamframe only for animation?",
    a: "We’re built for animation-first teams, but live-action pre-viz and voice/music workflows work great too.",
  },
  {
    q: "How do payments work?",
    a: "Fund project milestones into escrow. On approval of deliverables, payouts release instantly to creators.",
  },
  {
    q: "Can I bring my own collaborators?",
    a: "Absolutely. Invite your team, or use CCHI™ matching to fill gaps with verified pros.",
  },
  {
    q: "What does OTT-ready mean?",
    a: "We generate packaging: captions, metadata, formats (e.g., 4K ProRes), and deliverable bundles buyers expect.",
  },
    // NEW: Tool access clarity
    {
      q: "Do I have to use specific AI tools inside Dreamframe?",
      a: "No lock-in. Use your own accounts with your favorite AI tools (e.g., Runway, Pika, ElevenLabs) to create deliverables today. We’re actively partnering to integrate these platforms directly into the Dreamframe workflow over time.",
    },
  
    // NEW: Pricing justification
    {
      q: "Is Dreamframe Studio free?",
      a: "Yes — Dreamframe is free to join. Early creators get priority visibility in matching, faster profile discovery, and early access to upcoming collaboration tools. Creating a profile now helps you stand out as the creator network expands.",
    },    
    
];

const FAQ = () => (
  <section id="faq" className={`${section} mt-24 scroll-mt-24`}>
    <div className="mx-auto max-w-3xl text-center">
      <h2 className="text-3xl font-semibold text-white sm:text-4xl">FAQs</h2>
      <p className="mt-3 text-white/70">Quick answers to common questions.</p>
    </div>

    <div className="mx-auto mt-8 max-w-3xl space-y-4">
      {faqs.map((f) => (
        <details
          key={f.q}
          className="group rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur"
        >
          <summary className="flex cursor-pointer list-none items-center justify-between text-left text-white">
            <span className="text-base font-medium">{f.q}</span>
            <ArrowRight className="h-4 w-4 transition group-open:rotate-90" />
          </summary>
          <p className="mt-3 text-sm text-white/70">{f.a}</p>
        </details>
      ))}
    </div>
  </section>
);

// ---------- Footer ----------
const Footer = () => (
  <footer className="mt-24 border-t border-white/10">
    <div className={`${section} py-10`}>
      <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
        <div className="flex items-center gap-3">
        <img
            src={logo}
            alt="Dreamframe Studio Logo"
            className="h-7 w-auto rounded-lg"
            loading="lazy"
          />
          <span className="text-sm font-medium text-white">Dreamframe Studio</span>
        </div>
        <p className="text-center text-xs text-white/50 sm:text-right">
          © {new Date().getFullYear()} Dreamframe Studio. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// ---------- Instagram FAB ----------
const InstagramFAB = () => (
  <a
    className="fixed bottom-5 right-5 z-50 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/10 p-2 backdrop-blur transition hover:bg-white/20"
    href="https://www.instagram.com/dreamframestudi0/"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Dreamframe Instagram"
    title="Dreamframe Instagram"
  >
    <img
      className="h-7 w-7"
      alt="Instagram"
      src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png"
    />
  </a>
);

export default function Page() {
  return (
    <main className="min-h-screen bg-[#0b0b11] text-white">
      <BackgroundFX />
      <Navbar />
      <Hero />
      <Logos />
      <Divider />
      <Stats />
      <Features />
      <Divider />
      <HowItWorks />
      <RoleHowItWorksLite />
      <WhyJoin />
      <ProtectedCollab />
      <PromoVideo />
      <About />
      {/*<Showcase />*/}
      <Testimonials />
      {/*<Pricing />*/}
      <CTA />
      <FAQ />
      <Footer />
      <InstagramFAB />
    </main>
  );
}
